/**
 * WebhooksSection: the organisation's outgoing webhooks (W7, SU-2), next
 * to the API keys in Paramètres (admins only).
 *
 *   - one row per endpoint: URL, events, status, last delivery (state,
 *     HTTP code, attempts) and how many wait;
 *   - « Envoyer un test » sends a webhook.test now and says what came back;
 *   - « Mettre en pause » is reversible: it runs at once and offers
 *     « Annuler » in the toast (events keep queuing while paused);
 *   - « Changer le secret » and « Supprimer » ask first (DES-13);
 *   - the secret is shown once, at creation and at each rotation;
 *   - « Livraisons » lists the recent deliveries, each with « Renvoyer ».
 * Errors: translated message, then the code (DES-16).
 *
 * Server: /api/webhooks (server/src/adapters/inbound/http/webhooks.routes.ts);
 * contract for receivers: docs/webhooks.md.
 */
import { useState, type ComponentType, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, Loader2, MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox as UntypedCheckbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { copyText } from '@/components/shared/CopyLink';
import { usePermissions } from '@/hooks/usePermissions';
import { errorCodeOf, errorMessage } from '@/services/errorMessage';
import {
  WEBHOOK_EVENT_TYPES, createWebhook, deleteWebhook, listWebhookDeliveries, listWebhooks, pauseWebhook, replayWebhookDelivery,
  resumeWebhook, rotateWebhookSecret, testWebhook, type Webhook, type WebhookDelivery, type WebhookEventType,
} from '@/api/endpoints/webhooks';
import { OPENAPI_DOCS_URL } from './ApiKeysSection';

// components/ui/dialog is untyped (ts-nocheck): the props the secret dialog uses.
const LockedDialogContent = DialogContent as unknown as ComponentType<{
  className?: string; hideClose?: boolean; children?: ReactNode;
  onEscapeKeyDown?: (e: Event) => void; onInteractOutside?: (e: Event) => void;
}>;
import { SettingsCard } from './SettingsPrimitives';

// components/ui/checkbox is untyped JS.
const Checkbox = UntypedCheckbox as unknown as (props: { checked: boolean; onCheckedChange: (v: boolean | 'indeterminate') => void; 'aria-label'?: string }) => JSX.Element;

const QUERY_KEY = ['webhooks'];
type T = (key: string, options?: Record<string, unknown>) => string;

const fmt = (iso: string | null, lang: string) => (iso ? new Date(iso).toLocaleString(lang, { dateStyle: 'short', timeStyle: 'short' }) : '—');

function reportError(err: unknown, t: T, fallbackKey: string) {
  const code = errorCodeOf(err);
  toast.error(errorMessage(err, t, fallbackKey), code ? { description: code } : undefined);
}

/** « Livré », « Échec (HTTP 500) », « Nouvel essai à 10:42 »… */
function deliveryLabel(d: WebhookDelivery, t: T, lang: string): string {
  if (d.state === 'delivered') return t('settings:webhooks.state.delivered');
  if (d.state === 'cancelled') return t('settings:webhooks.state.cancelled');
  if (d.state === 'failed') return t('settings:webhooks.state.failed');
  if (d.attempts === 0) return t('settings:webhooks.state.queued');
  return t('settings:webhooks.state.retrying', { at: fmt(d.next_attempt_at, lang) });
}

function DeliveryCell({ d }: { d: WebhookDelivery | null }) {
  const { t, i18n } = useTranslation(['settings']);
  if (!d) return <span className="text-muted-foreground">{t('settings:webhooks.noDelivery')}</span>;
  const failed = d.state === 'failed' || (d.state === 'pending' && d.attempts > 0);
  return (
    <span className="text-xs">
      <span className={failed ? 'text-destructive' : 'text-foreground'}>{deliveryLabel(d, t, i18n.language)}</span>
      <span className="block font-mono text-[11px] text-muted-foreground">
        {d.last_status_code !== null ? t('settings:webhooks.httpStatus', { status: d.last_status_code }) : d.last_error ?? '—'} · {t('settings:webhooks.attempts', { n: d.attempts, max: d.max_attempts })}
      </span>
    </span>
  );
}

export function WebhooksSection() {
  const { t } = useTranslation(['settings', 'common']);
  const { isTenantAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Webhook | null>(null);
  const [rotating, setRotating] = useState<Webhook | null>(null);
  const [deliveriesOf, setDeliveriesOf] = useState<Webhook | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  const hooksQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal }) => (await listWebhooks(signal)).items,
    enabled: isTenantAdmin,
  });
  const hooks = hooksQuery.data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  if (!isTenantAdmin) return <p className="text-sm text-muted-foreground">{t('settings:webhooks.askAdmin')}</p>;

  const sendTest = async (hook: Webhook) => {
    setTesting(hook.id);
    try {
      const d = await testWebhook(hook.id);
      if (d.state === 'delivered') toast.success(t('settings:webhooks.testOk', { status: d.last_status_code }));
      else toast.error(t('settings:webhooks.testFailed'), { description: d.last_status_code !== null ? t('settings:webhooks.httpStatus', { status: d.last_status_code }) : d.last_error ?? undefined });
      refresh();
    } catch (err) {
      reportError(err, t, 'settings:webhooks.testError');
    } finally {
      setTesting(null);
    }
  };

  const setPaused = async (hook: Webhook, paused: boolean, undoable = true) => {
    try {
      await (paused ? pauseWebhook(hook.id) : resumeWebhook(hook.id));
      refresh();
      if (undoable) {
        toast.success(t(paused ? 'settings:webhooks.paused' : 'settings:webhooks.resumed'), {
          action: { label: t('common:actions.undo'), onClick: () => void setPaused(hook, !paused, false) },
        });
      }
    } catch (err) {
      reportError(err, t, 'settings:webhooks.statusFailed');
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await deleteWebhook(deleting.id);
      toast.success(t('settings:webhooks.deleted'));
      setDeleting(null);
      refresh();
    } catch (err) {
      reportError(err, t, 'settings:webhooks.deleteFailed');
      throw err;
    }
  };

  const rotate = async () => {
    if (!rotating) return;
    try {
      const res = await rotateWebhookSecret(rotating.id);
      setRotating(null);
      setSecret(res.secret);
      refresh();
    } catch (err) {
      reportError(err, t, 'settings:webhooks.rotateFailed');
      throw err;
    }
  };

  const columns: DataTableColumn<Webhook>[] = [
    {
      id: 'url',
      header: t('settings:webhooks.columns.url'),
      cell: (h) => (
        <span className="block min-w-0 max-w-[280px]">
          <span className="block truncate font-mono text-xs text-foreground" title={h.url}>{h.url}</span>
          {h.description ? <span className="block truncate text-xs text-muted-foreground">{h.description}</span> : null}
        </span>
      ),
    },
    {
      id: 'events',
      header: t('settings:webhooks.columns.events'),
      cell: (h) => <span className="text-xs">{h.event_types.map((e) => t(`settings:webhooks.events.${e}`)).join(', ')}</span>,
      hideable: true,
    },
    {
      id: 'status',
      header: t('settings:webhooks.columns.status'),
      cell: (h) => (
        <span className="text-xs">
          {t(h.status === 'paused' ? 'settings:webhooks.statusPaused' : 'settings:webhooks.statusActive')}
          {h.pending_deliveries > 0 ? <span className="block text-muted-foreground">{t('settings:webhooks.pending', { count: h.pending_deliveries })}</span> : null}
        </span>
      ),
    },
    { id: 'last', header: t('settings:webhooks.columns.last'), cell: (h) => <DeliveryCell d={h.last_delivery} /> },
    {
      id: 'actions',
      header: <span className="sr-only">{t('settings:webhooks.columns.actions')}</span>,
      align: 'right',
      cell: (h) => (
        <span className="inline-flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-11 text-xs" disabled={testing === h.id} onClick={() => void sendTest(h)}>
            {testing === h.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('settings:webhooks.test')}
          </Button>
          <Button variant="ghost" size="sm" className="h-11 text-xs" onClick={() => void setPaused(h, h.status === 'active')}>
            {t(h.status === 'active' ? 'settings:webhooks.pause' : 'settings:webhooks.resume')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-11 w-11 p-0" aria-label={t('settings:webhooks.moreAria', { url: h.url })}>
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setDeliveriesOf(h)}>{t('settings:webhooks.deliveries')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setRotating(h)}>{t('settings:webhooks.rotate')}</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onSelect={() => setDeleting(h)}>{t('settings:webhooks.delete')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      ),
    },
  ];

  const loadCode = hooksQuery.isError ? errorCodeOf(hooksQuery.error) : '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <a href={OPENAPI_DOCS_URL} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--vf-accent-text)' }}>
          <ExternalLink className="h-4 w-4" aria-hidden />
          {t('settings:webhooks.openapiLink')}
        </a>
        <Button size="sm" className="h-9" onClick={() => setCreating(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {t('settings:webhooks.create')}
        </Button>
      </div>

      <SettingsCard>
        <div className="p-3">
          <DataTable
            caption={t('settings:webhooks.caption')}
            columns={columns}
            rows={hooks}
            getRowId={(h) => h.id}
            status={hooksQuery.isLoading ? 'loading' : hooksQuery.isError ? 'error' : 'ready'}
            error={hooksQuery.isError ? { message: errorMessage(hooksQuery.error, t, 'settings:webhooks.loadFailed'), code: loadCode || undefined, onRetry: () => void hooksQuery.refetch() } : undefined}
            empty={{ title: t('settings:webhooks.empty'), action: <Button size="sm" onClick={() => setCreating(true)}>{t('settings:webhooks.create')}</Button> }}
            storageKey="settings-webhooks"
          />
        </div>
      </SettingsCard>

      <CreateWebhookDialog open={creating} onOpenChange={setCreating} onCreated={(s) => { setSecret(s); refresh(); }} />
      <SecretDialog secret={secret} onClose={() => setSecret(null)} />
      {deliveriesOf ? <DeliveriesDialog hook={deliveriesOf} onClose={() => { setDeliveriesOf(null); refresh(); }} /> : null}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => { if (!v) setDeleting(null); }}
        title={t('settings:webhooks.deleteTitle')}
        description={t('settings:webhooks.deleteWhy', { url: deleting?.url })}
        confirmLabel={t('settings:webhooks.deleteConfirm')}
        onConfirm={remove}
      />
      <ConfirmDialog
        open={!!rotating}
        onOpenChange={(v) => { if (!v) setRotating(null); }}
        title={t('settings:webhooks.rotateTitle')}
        description={t('settings:webhooks.rotateWhy')}
        confirmLabel={t('settings:webhooks.rotateConfirm')}
        tone="default"
        onConfirm={rotate}
      />
    </div>
  );
}

/**
 * The secret, once: after creation and after a rotation. REV-W7-F9: it is
 * shown only here, so only its button (« J'ai copié le secret ») closes the dialog (no Escape, no click
 * outside, no corner cross) and the secret cannot be lost by a stray key.
 */
function SecretDialog({ secret, onClose }: { secret: string | null; onClose: () => void }) {
  const { t } = useTranslation(['settings']);
  const copy = async () => {
    if (secret && await copyText(secret)) toast.success(t('settings:webhooks.copied'));
    else toast.error(t('settings:webhooks.copyFailed'));
  };
  return (
    <Dialog open={!!secret}>
      <LockedDialogContent
        className="max-w-lg"
        hideClose
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('settings:webhooks.secretTitle')}</DialogTitle>
          <DialogDescription>{t('settings:webhooks.secretWhy')}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 select-all break-all rounded bg-muted px-2 py-2 text-xs" data-testid="webhook-secret">{secret}</code>
          <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={() => void copy()}>
            <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />{t('settings:webhooks.copy')}
          </Button>
        </div>
        <DialogFooter>
          <Button size="sm" className="h-9" onClick={onClose}>{t('settings:webhooks.done')}</Button>
        </DialogFooter>
      </LockedDialogContent>
    </Dialog>
  );
}

function CreateWebhookDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: (secret: string) => void }) {
  const { t } = useTranslation(['settings', 'common']);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<WebhookEventType[]>(['call.completed']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code: string } | null>(null);

  const close = () => {
    if (busy) return;
    setUrl(''); setDescription(''); setEvents(['call.completed']); setError(null);
    onOpenChange(false);
  };
  const toggle = (e: WebhookEventType, on: boolean) => setEvents((cur) => (on ? [...new Set([...cur, e])] : cur.filter((x) => x !== e)));
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await createWebhook({ url: url.trim(), event_types: events, description: description.trim() || undefined });
      setBusy(false);
      setUrl(''); setDescription(''); setEvents(['call.completed']);
      onOpenChange(false);
      onCreated(res.secret);
    } catch (err) {
      setError({ message: errorMessage(err, t, 'settings:webhooks.createFailed'), code: errorCodeOf(err) });
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('settings:webhooks.createTitle')}</DialogTitle>
          <DialogDescription>{t('settings:webhooks.createWhy')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="webhook-url" className="text-xs">{t('settings:webhooks.url')}</Label>
            <Input id="webhook-url" className="mt-1 h-9" value={url} placeholder="https://" inputMode="url" onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="webhook-description" className="text-xs">{t('settings:webhooks.description')}</Label>
            <Input id="webhook-description" className="mt-1 h-9" value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <fieldset>
            <legend className="text-xs font-medium">{t('settings:webhooks.columns.events')}</legend>
            {WEBHOOK_EVENT_TYPES.map((e) => (
              <label key={e} className="flex min-h-11 items-center gap-2 text-sm">
                <Checkbox checked={events.includes(e)} onCheckedChange={(v) => toggle(e, v === true)} aria-label={t(`settings:webhooks.events.${e}`)} />
                <span>{t(`settings:webhooks.events.${e}`)} <code className="text-[11px] text-muted-foreground">{e}</code></span>
              </label>
            ))}
          </fieldset>
          {error ? (
            <div role="alert" className="text-xs">
              <p className="text-destructive">{error.message}</p>
              {error.code ? <p className="font-mono text-[11px] text-muted-foreground">{error.code}</p> : null}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" className="h-9" disabled={busy} onClick={close}>{t('common:actions.cancel')}</Button>
          <Button size="sm" className="h-9" disabled={!url.trim() || events.length === 0 || busy} onClick={() => void submit()}>
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('settings:webhooks.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesDialog({ hook, onClose }: { hook: Webhook; onClose: () => void }) {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const [replaying, setReplaying] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['webhook-deliveries', hook.id],
    queryFn: async ({ signal }) => (await listWebhookDeliveries(hook.id, signal)).items,
  });
  const replay = async (d: WebhookDelivery) => {
    setReplaying(d.id);
    try {
      await replayWebhookDelivery(d.id);
      toast.success(t('settings:webhooks.replayed'));
      void q.refetch();
    } catch (err) {
      reportError(err, t, 'settings:webhooks.replayFailed');
    } finally {
      setReplaying(null);
    }
  };
  const columns: DataTableColumn<WebhookDelivery>[] = [
    { id: 'event', header: t('settings:webhooks.columns.event'), cell: (d) => <code className="text-xs">{d.event_type}</code> },
    { id: 'state', header: t('settings:webhooks.columns.last'), cell: (d) => <DeliveryCell d={d} /> },
    { id: 'when', header: t('settings:webhooks.columns.when'), cell: (d) => <span className="text-xs">{fmt(d.last_attempt_at ?? d.created_at, i18n.language)}</span>, hideable: true },
    {
      id: 'actions',
      header: <span className="sr-only">{t('settings:webhooks.columns.actions')}</span>,
      align: 'right',
      // REV-W7-F13: a pending delivery is still being retried by the worker;
      // only a finished one (failed or delivered) can be sent again.
      cell: (d) => (d.state !== 'failed' && d.state !== 'delivered' ? null : (
        <Button variant="ghost" size="sm" className="h-11 text-xs" disabled={replaying === d.id} aria-label={t('settings:webhooks.replayAria', { event: d.event_type })} onClick={() => void replay(d)}>
          {t('settings:webhooks.replay')}
        </Button>
      )),
    },
  ];
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('settings:webhooks.deliveriesTitle')}</DialogTitle>
          <DialogDescription className="break-all font-mono text-xs">{hook.url}</DialogDescription>
        </DialogHeader>
        <DataTable
          caption={t('settings:webhooks.deliveriesTitle')}
          columns={columns}
          rows={q.data ?? []}
          getRowId={(d) => d.id}
          status={q.isLoading ? 'loading' : q.isError ? 'error' : 'ready'}
          error={q.isError ? { message: errorMessage(q.error, t, 'settings:webhooks.loadFailed'), code: errorCodeOf(q.error) || undefined, onRetry: () => void q.refetch() } : undefined}
          empty={{ title: t('settings:webhooks.noDeliveries') }}
        />
      </DialogContent>
    </Dialog>
  );
}
